import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import PeopleIcon from '@mui/icons-material/People';
import { StatusChip } from 'components/shared/StatusChip';

function SiteList({
  sites,
  loading,
  onEdit,
  onDelete,
  onTestConnection,
  onSyncCameras,
  onManageAccess,
  testingId,
  syncingId,
}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);

  const handleDeleteClick = (site) => {
    setSiteToDelete(site);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (siteToDelete) {
      onDelete(siteToDelete.id);
    }
    setDeleteDialogOpen(false);
    setSiteToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSiteToDelete(null);
  };

  const getStatusChip = (status) => (
    <StatusChip status={status} type="service" size="small" />
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.display_name || params.row.name}
          </Typography>
          {params.row.display_name && (
            <Typography variant="caption" color="text.secondary">
              {params.row.name}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'customer_name',
      headerName: 'Customer',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 90,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'camera_count',
      headerName: 'Cameras',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value || 0,
    },
    {
      field: 'last_check',
      headerName: 'Last Check',
      width: 160,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'response_time_ms',
      headerName: 'Response',
      width: 100,
      renderCell: (params) => (params.value ? `${params.value}ms` : '-'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const isTestingThis = testingId === params.row.id;
        const isSyncingThis = syncingId === params.row.id;

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => onEdit(params.row)}
                color="primary"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Manage Access">
              <IconButton
                size="small"
                onClick={() => onManageAccess(params.row)}
                color="primary"
              >
                <PeopleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Pending VMS migration">
              <span>
                <IconButton
                  size="small"
                  disabled
                  color="info"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Pending VMS migration">
              <span>
                <IconButton
                  size="small"
                  disabled
                  color="secondary"
                >
                  <SyncIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(params.row)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ height: 500, width: '100%' }}>
      <DataGrid
        rows={sites}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            py: 1,
          },
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Site</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the site{' '}
            <strong>{siteToDelete?.display_name || siteToDelete?.name}</strong>?
            <br />
            <br />
            This will also delete all associated cameras and events. This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SiteList;
